import { cloneElement, memo, useMemo } from 'react';
import classNames from 'classnames';

import SpinLoader, { LoaderSize } from 'Components/SpinLoader';

import instagramIcon from 'Assets/images/instagram-icon.svg';
import profilePlaceholder from 'Assets/images/loading-profile.jpg';

import { ImageLoaderType, Loaders } from '../constants';

const LOADER_CLASSNAME = 'D(f) Fxd(c) Ai(c) Jc(c) smart-image__loader';
const withSpinner = new Set([Loaders.SMALL, Loaders.NORMAL]);
const spinLoaderSize: Record<string, LoaderSize> = {
  [Loaders.SMALL]: 's',
  [Loaders.NORMAL]: 'l',
};

type Props = {
  className?: string;
  customLoader?: React.ReactElement;
  forwardedRef?: React.RefObject<any>;
  imgSrc?: string;
  isLoading?: boolean;
  type: ImageLoaderType;
};

const Loader = (props: Props) => {
  const { className, customLoader, forwardedRef, type } = props;
  const style: React.CSSProperties = useMemo(
    () => ({
      ...(type === Loaders.AVATAR_PLACEHOLDER
        ? { backgroundImage: `url(${profilePlaceholder})` }
        : {}),
      ...(type === Loaders.INSTAGRAM
        ? {
            backgroundImage: `url(${instagramIcon})`,
            backgroundSize: '50%',
          }
        : {}),
    }),
    [type]
  );

  const loaderNode = useMemo(() => {
    switch (type) {
      case Loaders.CUSTOM:
        if (customLoader) {
          const isReactComponent = typeof customLoader?.type === 'object';
          return cloneElement(customLoader, {
            ...(forwardedRef && isReactComponent ? { forwardedRef } : { ref: forwardedRef }),
          });
        }
        return <div className={className} ref={forwardedRef} />;
      case Loaders.NONE:
      case Loaders.SMALL:
      case Loaders.NORMAL:
      case Loaders.AVATAR_PLACEHOLDER:
      case Loaders.INSTAGRAM:
      default:
        return (
          <div
            className={classNames(className, {
              [LOADER_CLASSNAME]: withSpinner.has(type),
              'Bgc($c-ds-background-secondary) theme-dark_Fil($invert-img) Expand Bgz(cv) Bgp(c)':
                type === Loaders.AVATAR_PLACEHOLDER,
              'Bgp(c) Bgr(nr) Bgc($c-ds-background-secondary) Expand': type === Loaders.INSTAGRAM,
            })}
            style={style}
            ref={forwardedRef}
          >
            {withSpinner.has(type) && <SpinLoader size={spinLoaderSize[type]} />}
          </div>
        );
    }
  }, [className, customLoader, forwardedRef, type, style]);

  return loaderNode;
};

export default memo(Loader);
